import { useEffect, useState } from 'react';
import { purchasingAPI } from '../../../../lib/api';
import { Button } from '../../../ui/button';
import { Layers, Loader2 } from 'lucide-react';
import { OPEN_LINE_STATUSES, dueWindowParams } from '../po-lines/dueWindowParams';

interface LineSite {
  id: number;
  name: string;
}

interface MixedGroupLineSitesProps {
  /** The group's PO-line-grain sites (display settings ``group_po_line_sites``). */
  sites: LineSite[];
  /** Changes whenever the site switcher does, so the counts are re-read. */
  viewKey: string;
  /** Opens the PO Lines tab, where these sites' rows live. */
  onOpenPoLines: () => void;
}

type SiteCounts = Record<string, number | null>;

/**
 * Mixed-grain group on the Schedules tab: this grid browses schedule rows and
 * a PO-line-grain site has none, so its delivery work would be invisible here.
 * Names each such site with its own PO-line counts — the PO Lines tab's own
 * quick-filter params, narrowed to the site — so the group's whole book is on
 * the landing page, each site at its own grain, while the grid's tab counts
 * still equal the rows it lists.
 *
 * @param sites - PO-line-grain sites of the group in view
 * @param viewKey - view-context key; a switch refetches
 * @param onOpenPoLines - switches Purchasing to the PO Lines tab
 */
export default function MixedGroupLineSites({ sites, viewKey, onOpenPoLines }: MixedGroupLineSitesProps) {
  const [counts, setCounts] = useState<Record<number, SiteCounts>>({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (sites.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    Promise.all(sites.map((site) => purchasingAPI.getPOLineTabCounts({
      due_previous_weeks: { site: site.id, ...dueWindowParams('due_previous_weeks') },
      due_this_week: { site: site.id, ...dueWindowParams('due_this_week') },
      open: { site: site.id, status__in: OPEN_LINE_STATUSES },
    }).then((res) => [site.id, res.data.counts] as const)))
      .then((pairs) => { if (!cancelled) setCounts(Object.fromEntries(pairs)); })
      .catch((err: unknown) => {
        console.error('Failed to load PO-line site counts:', err);
        if (!cancelled) setFailed(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sites, viewKey]);

  if (sites.length === 0) return null;
  const fmt = (n: number | null | undefined) => (n == null ? '–' : n.toLocaleString());

  return (
    <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid="mixed-group-line-sites">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <Layers className="h-4 w-4 shrink-0" />
        <span className="font-medium">Also in this group, judged on PO lines:</span>
        {sites.map((site) => {
          const c = counts[site.id];
          return (
            <span key={site.id}>
              <span className="font-medium">{site.name}</span>{' '}
              {loading && !c ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : failed && !c ? 'counts unavailable' : (
                <>Due From Previous Weeks {fmt(c?.['due_previous_weeks'])} · Due This Week {fmt(c?.['due_this_week'])} · Open {fmt(c?.['open'])}</>
              )}
            </span>
          );
        })}
        <Button size="sm" variant="outline" className="ml-auto h-7" onClick={onOpenPoLines}>View on PO Lines</Button>
      </div>
      <p className="mt-1 text-xs text-sky-800">The schedule lines below are the group&apos;s schedule-grain sites; the tabs count those rows only.</p>
    </div>
  );
}
